import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useUnsavedChangesSource } from './appUpdate';
import { Box, Button, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { integrationApiService, IntegrationConfig, IntegrationSettings, StoreLayoutSettings } from '../services/integrationApi';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useToast } from '../contexts/ToastContext';
import { tokens } from '../theme/tokens';
import ShopifyCategoryMappingSection from './ShopifyCategoryMappingSection';
import type { SpecMetafieldRow } from './ShopifySpecMetafieldsSection';
import type { MetafieldMappingRow } from './ShopifyMetafieldMappingSection';
import { motzuKeySet, rulesForSave, specFillCount } from './mapping/mappingModel';
import MappingV2Panel, { fieldsAndListingsStatus, unmatchedCategoryCount } from './mapping/v2/MappingV2Panel';
import { useListingMatchesToConfirm } from './mapping/v2/useListingMatchesToConfirm';
import { DefaultMatchesView, DefaultSpecView } from './mapping/v2/slots';
import type { TaxonomyDrafts } from './mapping/v2/TaxonomyStep';
import MappingSectionLayout from './mapping/MappingSectionLayout';
import { MappingSection, MappingSectionLink, SECTION_OF_VIEW, mappingViewOf } from './mapping/mappingSections';
import { useAppTranslation } from '../i18n/useAppTranslation';

interface IntegrationMappingPanelProps {
  integration: IntegrationConfig;
  /**
   * The Mapping tab's open section (`?section=`) and its URL setter. Every section is a panel of this
   * one editor, so edits and the one save bar carry over when the store moves between them.
   */
  section?: MappingSectionLink | null;
  onSection?: (section: MappingSection) => void;
  onOpenProductFields?: () => void;
}

/**
 * Shopify mapping (the Mapping tab): product type and category, the store's own spec metafields
 * and listing matches, behind one section menu. Saves only the layout and spec keys, so it never
 * overwrites Sync Behavior.
 */
const IntegrationMappingPanel: React.FC<IntegrationMappingPanelProps> = ({
  integration,
  onOpenProductFields,
  section,
  onSection,
}) => {
  const queryClient = useQueryClient();
  const { t } = useAppTranslation();
  // Flag-off layout: one menu item per editor. Without a URL section it keeps its own selection.
  const [ownView, setOwnView] = useState(() => mappingViewOf(section));
  useEffect(() => {
    if (section !== undefined) setOwnView(mappingViewOf(section));
  }, [section]);
  const view = ownView;
  // Flag `shopify_mapping_v2`: the v2 layout renders in place of the views below; Save and Discard stay here.
  const { enabled: v2Flag } = useOrgFeatureFlag('shopify_mapping_v2');
  const { showToast } = useToast();
  const [storeLayout, setStoreLayout] = useState<StoreLayoutSettings | undefined>(integration.settings.storeLayout);
  const [specMetafields, setSpecMetafields] = useState<SpecMetafieldRow[]>(integration.settings.specMetafields || []);
  // MOT-144 (flag `shopify_metafield_mapping`): the general mapping rides the same Save as the spec rows.
  const { enabled: metafieldMappingFlag } = useOrgFeatureFlag('shopify_metafield_mapping');
  const [metafieldMappings, setMetafieldMappings] = useState<MetafieldMappingRow[]>(integration.settings.metafieldMappings || []);
  // v2 only: edits to the store's taxonomy entries, written by Save before the integration's own settings.
  const [taxonomyDrafts, setTaxonomyDrafts] = useState<TaxonomyDrafts>({});
  const [dirty, setDirtyState] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const dirtyRef = useRef(false);
  const seededIdRef = useRef<string | null>(null);
  const setDirty = (value: boolean) => {
    dirtyRef.current = value;
    setDirtyState(value);
  };

  useEffect(() => {
    if (dirtyRef.current && seededIdRef.current === integration.id) return;
    setStoreLayout(integration.settings.storeLayout);
    setSpecMetafields(integration.settings.specMetafields || []);
    setMetafieldMappings(integration.settings.metafieldMappings || []);
    seededIdRef.current = integration.id;
  }, [integration]);

  const optionsQuery = useQuery({
    queryKey: ['shopify-spec-metafield-options', integration.id],
    queryFn: () => integrationApiService.getSpecMetafieldOptions(integration.id),
    staleTime: 5 * 60 * 1000,
  });
  const fieldsQuery = useQuery({
    queryKey: ['motzu-metafield-fields'],
    queryFn: () => integrationApiService.getMotzuMetafieldFields(),
    staleTime: Infinity,
  });

  // Flag-off layout's menu lines, phrased as the v2 menu phrases them so both read alike.
  const fallbackCount = useMemo(() => unmatchedCategoryCount(storeLayout), [storeLayout]);
  const fill = useMemo(
    () => specFillCount(optionsQuery.data?.definitions || [], motzuKeySet(fieldsQuery.data || []), specMetafields),
    [optionsQuery.data, fieldsQuery.data, specMetafields],
  );
  // The shared listing-holds query, the same one the Catalog Matching tab badge reads.
  const matches = useListingMatchesToConfirm(integration);
  const status = fieldsAndListingsStatus(t, {
    specFilled: fill.filled,
    specTotal: fill.total,
    matches: matches.count,
    matchesCapped: matches.capped,
  });
  const M = 'integrations.shopifyMapping.menu';

  const save = useMutation({
    mutationFn: async () => {
      const draftIds = Object.keys(taxonomyDrafts);
      if (v2Flag && draftIds.length > 0) {
        const updated = await integrationApiService.updateMerchantTaxonomyEntries(
          draftIds.map((id) => ({ id, ...taxonomyDrafts[id] })),
        );
        queryClient.setQueryData(['merchant-taxonomy'], { registry: updated.registry, stats: updated.stats });
        setTaxonomyDrafts({});
      }
      const layout = storeLayout
        ? {
          ...storeLayout,
          productTypeRules: rulesForSave(storeLayout.productTypeRules),
          categoryMap: Object.fromEntries(
            Object.entries(storeLayout.categoryMap || {}).filter(([, value]) => String(value || '').trim()),
          ),
        }
        : storeLayout;
      // The general mapping is sent only with the flag on: off, the stored list is left exactly as it is.
      // A row still missing its source or target is not saved; the API would drop it anyway.
      const mappingRows = metafieldMappingFlag
        ? { metafieldMappings: metafieldMappings.filter((row) => row.sourceField && row.targetField) }
        : {};
      return integrationApiService.updateIntegration(integration.id, {
        settings: { storeLayout: layout, specMetafields, ...mappingRows } as IntegrationSettings,
      });
    },
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['integration', integration.id] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      if (v2Flag) queryClient.invalidateQueries({ queryKey: ['shopify-category-summary'] });
      showToast('Mapping saved. The next sync uses it.', 'success');
    },
    onError: (err: unknown) => {
      showToast(err instanceof Error ? err.message : 'Failed to save mapping', 'error');
    },
  });

  const discard = () => {
    setStoreLayout(integration.settings.storeLayout);
    setSpecMetafields(integration.settings.specMetafields || []);
    setMetafieldMappings(integration.settings.metafieldMappings || []);
    setTaxonomyDrafts({});
    setDirty(false);
    setEpoch((value) => value + 1);
  };

  // Lets the app-update prompt wait for, save or drop these edits.
  useUnsavedChangesSource({ dirty, save: () => save.mutateAsync(), discard });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {v2Flag ? (
        <MappingV2Panel
          integration={integration}
          section={section}
          onSection={onSection}
          storeLayout={storeLayout}
          onStoreLayoutChange={(next) => {
            setStoreLayout(next);
            setDirty(true);
          }}
          specMetafields={specMetafields}
          onSpecMetafieldsChange={(next) => {
            setSpecMetafields(next);
            setDirty(true);
          }}
          metafieldMappings={metafieldMappingFlag ? metafieldMappings : undefined}
          onMetafieldMappingsChange={(next) => {
            setMetafieldMappings(next);
            setDirty(true);
          }}
          metafieldMappingPlanLocked={Boolean(integration.planLocks?.customMetafieldMapping)}
          taxonomyDrafts={taxonomyDrafts}
          onTaxonomyDraftsChange={(next) => {
            setTaxonomyDrafts(next);
            if (Object.keys(next).length > 0) setDirty(true);
          }}
          epoch={epoch}
          onOpenProductFields={onOpenProductFields}
        />
      ) : (
        <MappingSectionLayout
          aria-label={t(`${M}.ariaLabel`)}
          active={SECTION_OF_VIEW[view]}
          onSelect={(next) => {
            setOwnView(mappingViewOf(next));
            onSection?.(next);
          }}
          groups={[
            {
              id: 'type',
              label: t(`${M}.groups.type`),
              items: [{
                id: 'owners',
                label: t(`${M}.items.typeAndCategory`),
                ...(fallbackCount > 0
                  ? { status: t(`${M}.status.unmatched`, { count: fallbackCount }), tone: 'warn' as const }
                  : { status: t(`${M}.status.allMapped`) }),
              }],
            },
            {
              id: 'fields',
              label: t(`${M}.groups.fields`),
              items: [
                { id: 'spec-metafields', label: t('integrations.shopifyDetail.tabs.metafields'), ...status.specs },
                { id: 'listing-matches', label: t('integrations.shopifyDetail.tabs.matching'), ...status.matches },
              ],
            },
          ]}
        >
          {/* Hidden, not unmounted, so each view keeps its scroll and open editors while the store looks elsewhere. */}
          <Box sx={{ display: view === 'type' ? 'flex' : 'none', flexDirection: 'column', gap: 2 }}>
            <ShopifyCategoryMappingSection
              key={`type-${epoch}`}
              integrationId={integration.id}
              layout={storeLayout}
              onChange={(next) => {
                setStoreLayout(next);
                setDirty(true);
              }}
            />
          </Box>

          <Box sx={{ display: view === 'specs' ? 'block' : 'none' }}>
            <DefaultSpecView
              key={`specs-${epoch}`}
              integration={integration}
              rows={specMetafields}
              onOpenProductFields={onOpenProductFields}
              onChange={(next) => {
                setSpecMetafields(next);
                setDirty(true);
              }}
              mappingRows={metafieldMappingFlag ? metafieldMappings : undefined}
              onMappingChange={(next) => {
                setMetafieldMappings(next);
                setDirty(true);
              }}
              mappingPlanLocked={Boolean(integration.planLocks?.customMetafieldMapping)}
            />
          </Box>

          <Box sx={{ display: view === 'matches' ? 'block' : 'none' }}>
            <DefaultMatchesView integration={integration} />
          </Box>
        </MappingSectionLayout>
      )}

      {dirty && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 16,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            px: 2,
            py: 1.25,
            bgcolor: tokens.surface.card,
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(15,23,42,.12)',
            border: '1px solid',
            borderColor: tokens.line.default,
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.signal.caution.solid, flex: '0 0 auto' }} />
          <Typography sx={{ fontSize: 13.5, color: tokens.ink.body, flex: '1 1 220px' }}>
            Unsaved mapping changes. The next sync uses them once saved.
          </Typography>
          <Button onClick={discard} disabled={save.isPending} sx={{ fontWeight: 600 }}>Discard</Button>
          <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending} sx={{ fontWeight: 700 }}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default IntegrationMappingPanel;
